<template>
  <div ref="boxComponent" class="container box_component">
    <div class="header-title">
      <h5 class="panel-title">{{ modeLabel }}</h5>
      <button v-if="isAthlete(user)" class="new-round-btn" @click="createNewRound">
        <i class="fa-solid fa-plus"></i> {{ $t('training.btn_new') }}
      </button>
    </div>

    <div class="filter-bar">
      <select v-model="selectedStatus" class="status-filter" @change="onFilterChange">
        <option value="">{{ $t('training.view.status') }}</option>
        <option v-for="status in uniqueStatuses" :key="status" :value="status">{{ status }}</option>
      </select>
      <select v-model="selectedRule" class="rule-filter" @change="onFilterChange">
        <option value="">{{ $t('training.view.rule') }}</option>
        <option v-for="rule in uniqueRules" :key="rule" :value="rule">{{ rule }}</option>
      </select>
    </div>

    <p v-if="isLoading" class="loading-text">{{ $t('training.loading') }}</p>

    <div class="training-grid" v-if="!isLoading" :class="['training-grid', visibleRounds.length <= 2 ? 'single' : '']">
      <p v-if="visibleRounds.length === 0" class="no-results">{{ $t('training.empty_rounds') }}</p>
      <template v-for="round in visibleRounds" :key="round.id">
        <div class="training-card" :class="getStatusClass(round.status_id)" @click.prevent="viewRound(round.id)">
          <div class="card-header" role="button" tabindex="0">
            <img :src="round.video_link ? youtubeVideo : notVideo" alt="Round Image" class="card-image">
          </div>
          <div class="card-body" role="button" tabindex="0">
            <p>{{ round.status.name }}</p>
            <template v-if="round.competition">
              <p><strong>{{ $t('training.competition') }}:</strong> {{ round.competition.name }}</p>
              <p><strong>{{ $t('training.round_number') }}:</strong> {{ round.round_number }}</p>
              <p v-if="round.round_size">
                <strong>{{ $t('training.round_size') }}:</strong> {{ round.round_size.name }}
              </p>
              <p><strong>{{ $t('training.total_score') }}:</strong> {{ formatScore(round.total_score) }}</p>
            </template>
            <template v-else>
              <p><strong>{{ $t('training.rules') }}:</strong> {{ round.rule.name }}</p>
              <p><strong>{{ $t('training.round_number') }}:</strong> {{ round.round_number }}</p>
              <p><strong>{{ $t('training.round_name') }}:</strong> {{ round.round_name }}</p>
              <p><strong>{{ $t('training.total_score') }}:</strong> {{ formatScore(round.total_score) }}</p>
            </template>
          </div>
          <div class="card-footer" @click.stop>
            <button
              :disabled="round.publish_at"
              class="action-btn edit"
              type="button"
              :title="$t('training.btn_edit')"
              @click.prevent="editRound(round.id)"
            >
              <i class="fa-solid fa-pencil-alt"></i>
            </button>
            <button
              :disabled="true"
              class="action-btn notify"
              type="button"
              :title="round.video_link ? $t('training.btn_publish') : $t('training.publish_complete')"
            >
              <i class="fa-solid fa-upload"></i>
            </button>
            <button
              :disabled="false"
              class="action-btn delete"
              type="button"
              :title="$t('training.btn_delete')"
              @click.prevent="deleteRound(round.id)"
            >
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
        </div>
      </template>
    </div>

    <div class="pagination-controls" v-if="showPagination">
      <button class="pagination-btn" @click="changePage(pagination.current_page - 1)" :disabled="pagination.current_page === 1 || isLoading">
        {{ $t('training.pagination.prev') || 'Anterior' }}
      </button>

      <span class="pagination-text">
        {{ $t('training.pagination.page') || 'Página' }} {{ pagination.current_page }} {{ $t('training.pagination.of') || 'de' }} {{ pagination.last_page }}
      </span>

      <button class="pagination-btn" @click="changePage(pagination.current_page + 1)" :disabled="pagination.current_page === pagination.last_page || isLoading">
        {{ $t('training.pagination.next') || 'Siguiente' }}
      </button>
    </div>
  </div>
  <ConfirmationModal
    ref="confirmationModal"
    :message="modalMessage"
    :showCancel="showCancel"
    :showRuleSelector="modalShowRuleSelector"
    :ruleOptions="ruleOptions"
    :showNameInput="modalShowNameInput"
    @confirmed="handleConfirmed"
    @canceled="cancelModal"
  />
</template>

<script>
import { isAthlete } from '@/helpers/roleUtils';
import { mapActions, mapState } from 'vuex';
import ConfirmationModal from '@/components/competitions/ConfirmationModal.vue';
import router from '@/router/router';
import { resolveResponsiveGridItemsPerPage } from '@/helpers/responsiveGridPagination';

export default {
  name: 'AthleteTrainingPanel',
  components: { ConfirmationModal },
  props: {
    modeLabel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      notVideo: require('@/assets/videoOff.jpg'),
      youtubeVideo: require('@/assets/videoOn.jpg'),
      isLoading: false,
      itemsPerPage: 6,
      currentPage: 1,
      pagination: null,
      resizeObserver: null,
      resizeFrame: null,
      observedContainerWidth: 0,
      pendingModalAction: null,
      pendingModalPayload: null,
      showCancel: false,
      modalMessage: '',
      modalShowRuleSelector: true,
      modalShowNameInput: true,
      selectedStatus: '',
      selectedRule: '',
      ruleOptions: [
        { id: 1, name: 'Without rules' },
        { id: 2, name: 'TJS v2.0' },
        { id: 3, name: 'TJS v3.0' },
        { id: 4, name: 'TJS v4.0' }
      ],
    };
  },
  computed: {
    ...mapState('user', ['isAuthenticated', 'user', 'token']),
    ...mapState('training', ['trainingRounds', 'trainingRoundsIdToView', 'trainingRoundsIdToCreate', 'trainingRoundsIdToEdit']),
    ...mapState('competition', ['competitionIdToEdit', 'competitionIdToView', 'appNavigation']),
    ...mapState('round', ['roundAthleteId', 'roundIdToUpdate', 'roundIdToView', 'competitionRounds', 'roundCompetitionIdToCreate', 'roundCompetitionIdToView', 'roundCompetitionIdToEdit', 'roundCompetitionIdToCreate', 'competitionPodiums', 'competitionRounds']),
    ...mapState('competition_users', ['competitionUsers']),

    filteredRounds() {
      return this.trainingRounds.filter(round => {
        const matchesStatus = this.selectedStatus ? round.status?.name === this.selectedStatus : true;
        const matchesRule = this.selectedRule ? round.rule?.name === this.selectedRule : true;
        return matchesStatus && matchesRule;
      });
    },
    visibleRounds() {
      return this.filteredRounds;
    },
    showPagination() {
      return Boolean(this.pagination && Number(this.pagination.last_page) > 1);
    },
    uniqueStatuses() {
      return [...new Set(this.trainingRounds.map(round => round.status?.name).filter(Boolean))];
    },
    uniqueRules() {
      return [...new Set(this.trainingRounds.map(round => round.rule?.name).filter(Boolean))];
    },
  },
  async mounted() {
    await this.fetchTrainingRounds();
    this.setupResponsivePagination();
    await this.clearCompetitionUsers();
    this.clearAppNavigationZone();
    this.clearCompetition();
    this.clearRound();
    this.clearPodium();
    this.clearCompetitionIdToView();
    this.clearCompetitionIdToEdit();
    this.clearTrainingRoundsIdToView();
    this.clearTrainingRoundsIdToCreate();
    this.clearTrainingRoundsIdToEdit();
    this.clearCompetitionRoundIdToView();
    this.clearCompetitionRoundIdToCreate();
    this.clearCompetitionRoundIdToEdit();
    localStorage.removeItem('validatedTricks');
    localStorage.removeItem('selectedTricks');
    localStorage.removeItem('comboCount');
    localStorage.removeItem('videoUrl');
  },
  beforeUnmount() {
    this.teardownResponsivePagination();
  },
  methods: {
    ...mapActions('training', ['setTrainingRounds', 'setTrainingRoundsIdToView', 'setTrainingRoundsIdToCreate', 'setTrainingRoundsIdToEdit', 'clearTrainingRoundsIdToView', 'clearTrainingRoundsIdToCreate', 'clearTrainingRoundsIdToEdit', 'clearTrainingRounds']),
    ...mapActions('competition', ['updateAppNavigationZone', 'clearAppNavigationZone', 'clearCompetitionIdToView', 'clearCompetitionIdToEdit', 'clearCompetition']),
    ...mapActions('round', ['clearRoundIdToUpdate', 'clearRoundIdToView', 'clearRoundAthleteId', 'clearCompetitionRoundIdToCreate', 'clearCompetitionRoundIdToView', 'clearCompetitionRoundIdToEdit', 'clearRound', 'clearPodium']),
    ...mapActions('competition_users', ['clearCompetitionUsers']),
    isAthlete,
    setupResponsivePagination() {
      this.$nextTick(() => {
        this.scheduleItemsPerPageUpdate();
        window.addEventListener('resize', this.scheduleItemsPerPageUpdate);
        window.addEventListener('orientationchange', this.scheduleItemsPerPageUpdate);

        if (typeof ResizeObserver === 'undefined') {
          return;
        }

        const target = this.$refs.boxComponent;
        if (!target) {
          return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
          const entry = entries?.[0];
          const nextWidth = Math.round(entry?.contentRect?.width || 0);
          if (!nextWidth || nextWidth === this.observedContainerWidth) {
            return;
          }

          this.observedContainerWidth = nextWidth;
          this.scheduleItemsPerPageUpdate();
        });
        this.resizeObserver.observe(target);
      });
    },
    teardownResponsivePagination() {
      window.removeEventListener('resize', this.scheduleItemsPerPageUpdate);
      window.removeEventListener('orientationchange', this.scheduleItemsPerPageUpdate);
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    scheduleItemsPerPageUpdate() {
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateItemsPerPage();
      });
    },
    async updateItemsPerPage() {
      const target = this.$refs.boxComponent;
      if (!target) {
        return;
      }

      const containerWidth = Math.round(target.clientWidth || 0);
      if (!containerWidth) {
        return;
      }

      this.observedContainerWidth = containerWidth;
      const nextItemsPerPage = resolveResponsiveGridItemsPerPage(containerWidth, window.innerWidth, {
        desktopMinCardWidth: 280,
        mobileMinCardWidth: 280,
        desktopGridGap: 15,
        mobileGridGap: 15,
        rowsPerPage: 2,
      });

      if (nextItemsPerPage === this.itemsPerPage) {
        return;
      }

      const currentOffset = Math.max(this.currentPage - 1, 0) * this.itemsPerPage;
      this.itemsPerPage = nextItemsPerPage;
      const nextPage = Math.max(1, Math.floor(currentOffset / this.itemsPerPage) + 1);
      await this.fetchTrainingRounds(nextPage);
    },
    createNewRound() {
      this.openConfirmationModal({
        action: 'createRound',
        message: this.$t('training.modal.create_confirm'),
        showRuleSelector: true,
        showNameInput: true,
      });
    },
    async fetchTrainingRounds(page = this.currentPage) {
      this.showLoading();
      try {
        const response = await this.$axios.get('/training/list', {
          params: {
            type: 'athlete',
            page: Number(page) || 1,
            per_page: this.itemsPerPage,
          },
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        const payload = response?.data;
        const items = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.data) ? payload.data : []);

        await this.setTrainingRounds(items);
        this.currentPage = Number(page) || 1;
        this.pagination = this.normalizePagination(payload?.pagination || null, this.currentPage, this.itemsPerPage);
      } catch (error) {
        console.error('❌ Error al obtener los rounds:', error);
        await this.setTrainingRounds([]);
        this.pagination = null;
      } finally {
        this.hideLoading();
      }
    },
    normalizePagination(rawPagination, fallbackPage = 1, fallbackPerPage = 6) {
      const source = rawPagination && typeof rawPagination === 'object' ? rawPagination : {};
      const currentPage = Number(source.current_page ?? source.currentPage ?? fallbackPage) || fallbackPage;
      const perPage = Number(source.per_page ?? source.perPage ?? fallbackPerPage) || fallbackPerPage;
      const total = Number(source.total ?? source.total_items ?? source.totalItems ?? 0) || 0;
      const count = Number(source.count ?? source.item_count ?? source.itemCount ?? 0) || 0;
      const lastPage = Number(source.last_page ?? source.lastPage ?? (perPage > 0 ? Math.ceil(total / perPage) : 1)) || 1;

      return {
        current_page: Math.max(1, currentPage),
        per_page: Math.max(1, perPage),
        total: Math.max(0, total),
        count: Math.max(0, count),
        last_page: Math.max(1, lastPage),
        has_more_pages: Boolean(source.has_more_pages ?? source.hasMorePages ?? currentPage < lastPage),
        next_page: Number(source.next_page ?? source.nextPage ?? (currentPage < lastPage ? currentPage + 1 : null)) || null,
        prev_page: Number(source.prev_page ?? source.prevPage ?? (currentPage > 1 ? currentPage - 1 : null)) || null,
      };
    },
    showLoading() {
      this.isLoading = true;
      this.$nextTick(() => {
        if (this.$root.$refs.globalLoading) {
          this.$root.$refs.globalLoading.showLoading();
        }
      });
    },
    hideLoading() {
      this.isLoading = false;
      this.$nextTick(() => {
        if (this.$root.$refs.globalLoading) {
          this.$root.$refs.globalLoading.hideLoading();
        }
      });
    },
    async viewRound(id) {
      try {
        this.showLoading();

        await this.clearCompetitionRoundIdToView();
        await this.clearCompetitionRoundIdToEdit();
        await this.clearCompetitionRoundIdToCreate();
        await this.clearRoundIdToView();
        await this.clearRoundIdToUpdate();
        await this.clearCompetitionIdToView();
        await this.clearCompetitionIdToEdit();

        this.setTrainingRoundsIdToView(id);
        this.updateAppNavigationZone({
          zone: 'training',
          data: {
            cameFromTrainingZone: true,
            currentPage: this.currentPage,
          }
        });
        await router.push('/score');
      } catch (error) {
        this.hideLoading();
        this.modalMessage = this.$t('training.modal.unexpected_error');
        this.showCancel = false;
        this.$refs.confirmationModal.openModal();
      }
    },
    async editRound(id) {
      try {
        this.showLoading();

        await this.clearCompetitionRoundIdToView();
        await this.clearCompetitionRoundIdToEdit();
        await this.clearCompetitionRoundIdToCreate();
        await this.clearRoundIdToView();
        await this.clearRoundIdToUpdate();
        await this.clearCompetitionIdToView();
        await this.clearCompetitionIdToEdit();

        this.setTrainingRoundsIdToEdit(id);
        this.updateAppNavigationZone({
          zone: 'training',
          data: {
            cameFromTrainingZone: true,
            currentPage: this.currentPage,
          }
        });
        await router.push('/score');
      } catch (error) {
        this.hideLoading();
        this.modalMessage = this.$t('training.modal.unexpected_error');
        this.showCancel = false;
        this.$refs.confirmationModal.openModal();
      }
    },
    async deleteRound(id) {
      this.openConfirmationModal({
        action: 'deleteRound',
        message: 'Are you sure you want to delete this round?',
        payload: id,
        showRuleSelector: false,
        showNameInput: false,
      });
    },

    formatScore(score) {
      const value = parseFloat(score);
      return Number.isFinite(value) ? value.toFixed(2) : '—';
    },

    getStatusClass(statusId) {
      switch (statusId) {
        case 3:
          return 'status-pending';
        case 1:
          return 'status-registered';
        case 4:
          return 'status-approved';
        case 10:
          return 'status-published';
        case 15:
          return 'status-started';
        case 8:
          return 'status-finished';
        default:
          return 'status-unknown';
      }
    },
    async handleConfirmed({ selectedRule, customName }) {
      const action = this.pendingModalAction;
      const payload = this.pendingModalPayload;
      this.resetConfirmationModalState();

      if (action === 'deleteRound') {
        this.showLoading();
        try {
          const response = await this.$axios.delete(`/delete_round/${payload}`, {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          });

          if (response.status === 200) {
            await this.fetchTrainingRounds(this.currentPage || 1);
          } else {
            throw new Error('Error en el servidor: no se pudo eliminar el registro.');
          }

          this.hideLoading();
        } catch (error) {
          console.error('Error al eliminar el registro:', error.response ? error.response.data : error.message);
          this.hideLoading();
        }
        return;
      }

      try {
        this.showLoading();

        await this.setTrainingRoundsIdToCreate({ selectedRule, customName });

        this.updateAppNavigationZone({
          zone: 'training',
          data: {
            cameFromTrainingZone: true,
            currentPage: this.currentPage,
          }
        });

        this.hideLoading();
        await router.push('/score');
      } catch (error) {
        this.hideLoading();
        this.modalMessage = this.$t('training.modal.unexpected_error');
        this.showCancel = false;
        this.$refs.confirmationModal.openModal();
      }
    },
    async confirmModal() {
      console.log('🔥 Modal Confirmado');
    },
    cancelModal() {
      this.resetConfirmationModalState();
    },
    resetConfirmationModalState() {
      this.pendingModalAction = null;
      this.pendingModalPayload = null;
      this.showCancel = false;
      this.modalMessage = '';
      this.modalShowRuleSelector = true;
      this.modalShowNameInput = true;
    },
    openConfirmationModal({ action, message, payload = null, showRuleSelector = true, showNameInput = true, showCancel = true }) {
      this.pendingModalAction = action;
      this.pendingModalPayload = payload;
      this.showCancel = showCancel;
      this.modalMessage = message;
      this.modalShowRuleSelector = showRuleSelector;
      this.modalShowNameInput = showNameInput;
      this.$refs.confirmationModal.openModal();
    },
    async onFilterChange() {
      this.currentPage = 1;
      await this.fetchTrainingRounds(1);
    },
    async changePage(page) {
      const requestedPage = Number(page) || 1;
      if (requestedPage <= 0) return;
      if (requestedPage === this.currentPage) return;

      await this.fetchTrainingRounds(requestedPage);
    },
  }
};
</script>

<style scoped>
.box_component {
  width: 80%;
  max-width: 80%;
  margin: 15px auto;
}

@media (max-width: 576px) {
  .box_component {
    width: 100%;
    max-width: 100%;
  }
}
.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.panel-title {
  margin: 0;
  font-weight: 600;
  color: #374151;
}
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}
.rule-filter, .status-filter {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.new-round-btn {
  background-color: #28a745;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.new-round-btn:hover {
  background-color: #218838;
}
.loading-text {
  text-align: center;
  font-size: 16px;
  color: #666;
}
.training-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
  justify-content: center;
  margin: 0 auto;
}
.training-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  text-align: center;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: box-shadow 0.3s ease;
  margin-bottom: 15px;
  width: 100%;
  max-width: 320px;
  justify-self: center;
}
.status-pending {
  box-shadow: 0 0 10px rgba(255, 165, 0, 0.6);
}
.status-approved {
  box-shadow: 0 0 10px rgba(0, 128, 0, 0.6);
}
.status-published {
  box-shadow: 0 0 10px rgba(0, 0, 255, 0.6);
}
.status-started {
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.6);
}
.status-finished {
  box-shadow: 0 0 10px rgba(128, 128, 128, 0.6);
}
.status-registered {
  box-shadow: 0 0 10px rgba(66, 60, 98, 0.6);
}
.status-unknown {
  box-shadow: 0 0 10px rgba(145, 23, 23, 0.6);
}
.card-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  opacity: 0.7;
}
.card-footer {
  display: flex;
  justify-content: space-around;
  padding: 10px;
  background: #f8f9fa;
  border-top: 1px solid #ddd;
}
.action-btn {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 5px;
  transition: color 0.3s ease;
}
.action-btn.notify { color: #5b5b5b; }
.action-btn.edit { color: #5b5b5b; }
.action-btn.delete { color: #5b5b5b; }
.action-btn:hover {
  opacity: 0.7;
}
.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.action-btn:disabled i {
  color: gray !important;
}

.no-results {
  text-align: center;
  color: #999;
  font-size: 14px;
  margin-top: 20px;
}
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.pagination-btn {
  background-color: #4c56af;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #3b4491;
}

.pagination-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.pagination-text {
  font-size: 14px;
  color: #444;
}
@media (max-width: 576px) {
  .training-card{
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
  }
}
</style>
